import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSession } from '@clerk/clerk-react';
import { Input, Spinner } from '@heroui/react';
import { MagnifyingGlass, Plus, User, Buildings, Phone, EnvelopeSimple } from '@phosphor-icons/react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { toast } from 'react-toastify';
import { ResourceType } from 'shared/types/resource';

// Allow resourceId to be string or number for compatibility
export interface ResourceValue {
    id?: number | string;
    type?: ResourceType;
    resourceId?: string | number;
    name?: string;
    // Extended contact details
    phone?: string;
    email?: string;
    companyName?: string;
    companyId?: number | string;
    functionTitle?: string;
}

interface ResourceSearchSelectProps {
    value: ResourceValue | null;
    onChange: (resource: ResourceValue | null) => void;
    resourceFilter?: ResourceType[];
    placeholder?: string;
    label?: string;
    error?: string;
    // Callback when user wants to create a new contact
    onCreateContact?: (searchTerm: string) => void;
    // Whether modal is open (to hide dropdown)
    isCreateModalOpen?: boolean;
}

interface SearchResult {
    id: number;
    name: string;
    type: ResourceType;
    resourceId: string;
    phone?: string;
    email?: string;
    companyName?: string;
    companyId?: number;
    functionTitle?: string;
}

export const ResourceSearchSelect: React.FC<ResourceSearchSelectProps> = ({
    value,
    onChange,
    resourceFilter = [ResourceType.CONTACT, ResourceType.COMPANY],
    placeholder = "Zoek contact of bedrijf...",
    label,
    error,
    onCreateContact,
    isCreateModalOpen = false
}) => {
    const { session } = useSession();
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Close dropdown when clicking outside (but not when modal is open)
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isCreateModalOpen) return; // Don't close dropdown when modal is open
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isCreateModalOpen]);

    // Search resources (contacts and companies)
    const searchResources = useCallback(async (query: string) => {
        if (!query || query.length < 2) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        try {
            const results: SearchResult[] = [];

            // Search contacts if included in filter
            if (resourceFilter.includes(ResourceType.CONTACT)) {
                const contactResponse = await fetchWithAuth(
                    `${API_URL}/contact/search?query=${encodeURIComponent(query)}`,
                    { method: 'GET' },
                    session
                );

                if (contactResponse.ok) {
                    const contacts = await contactResponse.json();
                    contacts.forEach((contact: any) => {
                        results.push({
                            id: contact.id,
                            name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
                            type: ResourceType.CONTACT,
                            resourceId: contact.id.toString(),
                            phone: contact.phone_mobile || contact.phone_direct,
                            email: contact.email,
                            companyName: contact.company?.name,
                            companyId: contact.company?.id,
                            functionTitle: contact.function || contact.jobTitle
                        });
                    });
                }
            }

            // Search companies if included in filter
            if (resourceFilter.includes(ResourceType.COMPANY)) {
                const companyResponse = await fetchWithAuth(
                    `${API_URL}/contact/search-companies?query=${encodeURIComponent(query)}`,
                    { method: 'GET' },
                    session
                );

                if (companyResponse.ok) {
                    const companies = await companyResponse.json();
                    companies.forEach((company: any) => {
                        results.push({
                            id: company.id,
                            name: company.name,
                            type: ResourceType.COMPANY,
                            resourceId: company.id.toString(),
                            phone: company.phone_direct,
                            email: company.email
                        });
                    });
                }
            }

            setSearchResults(results);
        } catch (error) {
            console.error('Error searching resources:', error);
            toast.error('Fout bij zoeken');
        } finally {
            setIsSearching(false);
        }
    }, [session, resourceFilter]);

    // Debounced search
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (searchTerm.length >= 2) {
            searchTimeoutRef.current = setTimeout(() => {
                searchResources(searchTerm);
            }, 300);
        } else {
            setSearchResults([]);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm, searchResources]);

    // Handle selection
    const handleSelect = useCallback((result: SearchResult) => {
        onChange({
            id: result.id,
            type: result.type,
            resourceId: result.resourceId,
            name: result.name,
            phone: result.phone,
            email: result.email,
            companyName: result.companyName,
            companyId: result.companyId,
            functionTitle: result.functionTitle
        });
        setShowDropdown(false);
        setSearchTerm('');
    }, [onChange]);

    // Handle create contact click
    const handleCreateClick = useCallback(() => {
        if (onCreateContact) {
            onCreateContact(searchTerm);
        }
    }, [onCreateContact, searchTerm]);

    // Clear selection and show search input
    const handleClear = useCallback(() => {
        onChange(null);
        setSearchTerm('');
        // Small delay to ensure the input is rendered before focusing
        setTimeout(() => {
            setShowDropdown(true);
        }, 50);
    }, [onChange]);

    // Clear search term when value is selected (from external modal)
    useEffect(() => {
        if (value?.name) {
            setSearchTerm('');
            setShowDropdown(false);
        }
    }, [value?.name]);

    const getIcon = (type: ResourceType) => {
        return type === ResourceType.CONTACT
            ? <User className="h-4 w-4 text-neutral-400" />
            : <Buildings className="h-4 w-4 text-neutral-400" />;
    };

    const getTypeLabel = (type: ResourceType) => {
        return type === ResourceType.CONTACT ? 'Contact' : 'Bedrijf';
    };

    return (
        <div ref={containerRef} className="relative w-full">
            {label && (
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                    {label}
                </label>
            )}

            {/* Selected value display or search input */}
            {value && value.name ? (
                <div className="bg-white border border-neutral-primary/20 rounded-lg overflow-hidden">
                    {/* Header with name and type */}
                    <div className="flex items-center gap-3 px-3 py-2.5 bg-neutral-50/50 border-b border-neutral-100">
                        <div className="flex-shrink-0">
                            {value.type === ResourceType.CONTACT ? (
                                <div className="w-8 h-8 rounded-full bg-moss-primary/10 flex items-center justify-center">
                                    <User className="h-4 w-4 text-moss-primary" weight="fill" />
                                </div>
                            ) : (
                                <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center">
                                    <Buildings className="h-4 w-4 text-sky-600" weight="fill" />
                                </div>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-medium text-neutral-900">{value.name}</div>
                            {value.type === ResourceType.CONTACT && value.functionTitle && (
                                <div className="text-xs text-neutral-500">{value.functionTitle}</div>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={handleClear}
                            className="text-sm text-moss-primary hover:text-moss-primary/80 font-medium px-2 py-1 rounded hover:bg-moss-primary/10 transition-colors"
                        >
                            Wijzig
                        </button>
                    </div>
                    {/* Details section */}
                    <div className="px-3 py-2 space-y-1.5">
                        {/* Company for contacts */}
                        {value.type === ResourceType.CONTACT && (
                            <div className="flex items-center gap-2 text-sm">
                                <Buildings className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                                <span className={value.companyName ? "text-neutral-700" : "text-neutral-400 italic"}>
                                    {value.companyName || "Geen bedrijf"}
                                </span>
                            </div>
                        )}
                        {/* Phone */}
                        <div className="flex items-center gap-2 text-sm">
                            <Phone className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                            <span className={value.phone ? "text-neutral-700" : "text-neutral-400 italic"}>
                                {value.phone || "Geen telefoon"}
                            </span>
                        </div>
                        {/* Email */}
                        <div className="flex items-center gap-2 text-sm">
                            <EnvelopeSimple className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                            <span className={value.email ? "text-neutral-700 truncate" : "text-neutral-400 italic"}>
                                {value.email || "Geen e-mail"}
                            </span>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="relative">
                    <Input
                        placeholder={placeholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onFocus={() => setShowDropdown(true)}
                        startContent={<MagnifyingGlass className="h-4 w-4 text-neutral-400" />}
                        endContent={isSearching && <Spinner size="sm" />}
                        classNames={{
                            base: "w-full",
                            inputWrapper: "bg-white h-10"
                        }}
                    />
                </div>
            )}

            {/* Dropdown with search results */}
            {showDropdown && !value?.name && !isCreateModalOpen && (
                <div className="absolute left-0 right-0 top-full mt-1 z-50 max-h-64 overflow-auto rounded-lg border border-neutral-primary/20 bg-white shadow-lg">
                    {/* Search results */}
                    {searchTerm.length >= 2 && (
                        <>
                            {searchResults.length > 0 && (
                                <div className="px-3 py-2 text-xs font-semibold text-neutral-500 bg-neutral-50 border-b">
                                    Zoekresultaten
                                </div>
                            )}
                            {searchResults.map((result) => (
                                <div
                                    key={`${result.type}-${result.id}`}
                                    onClick={() => handleSelect(result)}
                                    className="flex items-start gap-2 px-3 py-2.5 cursor-pointer hover:bg-moss-primary/10 border-b border-neutral-100"
                                >
                                    <div className="flex-shrink-0 mt-0.5">
                                        {getIcon(result.type)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-medium">{result.name}</span>
                                            <span className="text-xs px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500">
                                                {getTypeLabel(result.type)}
                                            </span>
                                        </div>
                                        {/* Company and function for contacts */}
                                        {result.type === ResourceType.CONTACT && (result.companyName || result.functionTitle) && (
                                            <div className="text-xs text-neutral-500 mt-0.5">
                                                {result.functionTitle}
                                                {result.functionTitle && result.companyName && ' @ '}
                                                {result.companyName && (
                                                    <span className="font-medium">{result.companyName}</span>
                                                )}
                                            </div>
                                        )}
                                        {/* Phone and email */}
                                        {(result.phone || result.email) && (
                                            <div className="flex items-center gap-3 mt-1 flex-wrap text-xs text-neutral-400">
                                                {result.phone && (
                                                    <span className="flex items-center gap-1">
                                                        <Phone className="h-3 w-3" />
                                                        {result.phone}
                                                    </span>
                                                )}
                                                {result.email && (
                                                    <span className="flex items-center gap-1 truncate max-w-[200px]">
                                                        <EnvelopeSimple className="h-3 w-3" />
                                                        {result.email}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {/* No results message */}
                            {searchResults.length === 0 && !isSearching && (
                                <div className="px-3 py-4 text-center text-sm text-neutral-500">
                                    Geen resultaten gevonden voor "{searchTerm}"
                                </div>
                            )}
                        </>
                    )}

                    {/* Instruction when not searching */}
                    {searchTerm.length < 2 && (
                        <div className="px-3 py-4 text-center text-sm text-neutral-500">
                            Typ minstens 2 tekens om te zoeken
                        </div>
                    )}

                    {/* Create new contact button - only show if contacts are in filter and callback is provided */}
                    {resourceFilter.includes(ResourceType.CONTACT) && onCreateContact && (
                        <div
                            onClick={handleCreateClick}
                            className="flex items-center gap-2 px-3 py-3 cursor-pointer hover:bg-moss-primary/10 bg-neutral-50 border-t"
                        >
                            <Plus className="h-4 w-4 text-moss-primary" />
                            <span className="text-sm font-medium text-moss-primary">
                                Nieuw contact aanmaken{searchTerm && `: "${searchTerm}"`}
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* Error message */}
            {error && (
                <p className="text-brick-primary text-sm mt-1">{error}</p>
            )}
        </div>
    );
};

export default ResourceSearchSelect;
